import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReportBudgetRow, { ReportBudgetRowWrapper } from './ReportBudgetRow';
import ReportBudgetHeader from './ReportBudgetHeader';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { ReportAnswers } from '../../FeedbackReport';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { findVariance, numberWithCommas } from 'shared/utils/numberUtils';
import {
  BUDGET_FILE_QUESTION_ID,
  DIRECT_COST_QUESTION_ID,
  FINAL_BUDGET_QUESTION_ID,
  INTERIM_BUDGET_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  getBudgetDollarAmount,
  getFinalizedBudget,
  getTotalBudget,
} from 'npo/pages/ReportBuilder/Components/Budget/utils';
import { defaultCostValue } from 'npo/pages/ReportBuilder/Components/Budget/RelevancyCheckbox';
import { parseAnswer } from 'npo/pages/BetaFeedback/Feedback/FeedbackAnswer';
import JSON5 from 'json5';
import TrueImpactFileRender from 'shared/design-system/components/TrueImpactFileRender';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import {
  BudgetFileSummary,
  getBudgetFiles,
  getReportNotes,
  mapTrueImpactFile,
  ReportNoteSummary,
} from './api';
import { renderTextWithParagraphs } from '../ReportNarrative';

const parseBudgetFile = (file: string | undefined) => {
  if (!file) return undefined;
  try {
    return JSON5.parse(file);
  } catch (error) {
    return undefined;
  }
};

const getCostValue = (cost: string | undefined) => {
  return cost === defaultCostValue ? cost : cost === undefined ? 'N/A' : cost;
};

const getBudgetVariation = (
  initial: string | undefined,
  final: string | undefined,
): number | undefined => {
  if (initial === defaultCostValue || final === defaultCostValue) {
    return undefined;
  }
  return findVariance(initial, final);
};

export const getBudgetFromAnswers = (answers: ReportAnswers) => {
  const finalBudget = getFinalizedBudget(
    answers.finalize_report?.find(
      x => x.question.dbId === FINAL_BUDGET_QUESTION_ID,
    )?.text,
  );
  const interimBudget = getFinalizedBudget(
    answers.interim_report?.find(
      x => x.question.dbId === INTERIM_BUDGET_QUESTION_ID,
    )?.text,
  );
  const initialDirectCost = getBudgetDollarAmount(
    answers.budget?.find(x => x.question.dbId === DIRECT_COST_QUESTION_ID)
      ?.text,
  );
  const initialOverheadCost = getBudgetDollarAmount(
    answers.budget?.find(x => x.question.dbId === OVERHEAD_COST_QUESTION_ID)
      ?.text,
  );
  const initialInKindDonations = getBudgetDollarAmount(
    answers.budget?.find(x => x.question.dbId === IN_KIND_DONATIONS_QUESTION_ID)
      ?.text,
  );
  const budgetSummaryAnswer = answers.budget?.find(
    x => x.question.dbId === 80011,
  );
  const budgetFile = parseBudgetFile(
    answers.budget?.find(x => x.question.dbId === BUDGET_FILE_QUESTION_ID)
      ?.text,
  );
  const budgetSummary = budgetSummaryAnswer
    ? parseAnswer(budgetSummaryAnswer)
    : undefined;

  const directCost =
    finalBudget?.directCost ||
    interimBudget?.directCost ||
    budgetSummary?.directCost ||
    initialDirectCost;
  const overheadCost =
    finalBudget?.overheadCost ||
    interimBudget?.overheadCost ||
    budgetSummary?.overheadCost ||
    initialOverheadCost;
  const inKindDonations =
    finalBudget?.inKindDonations ||
    interimBudget?.inKindDonations ||
    budgetSummary?.inKindDonations ||
    initialInKindDonations;

  const totalBudget = getTotalBudget(directCost, overheadCost, inKindDonations);
  return {
    directCost,
    initialDirectCost,
    finalBudget,
    overheadCost,
    initialOverheadCost,
    inKindDonations,
    initialInKindDonations,
    totalBudget,
    directCostVariance: getBudgetVariation(
      initialDirectCost,
      finalBudget?.directCost || interimBudget?.directCost,
    ),
    overheadCostVariance: getBudgetVariation(
      initialOverheadCost,
      finalBudget?.overheadCost || interimBudget?.overheadCost,
    ),
    inKindDonationVariance: getBudgetVariation(
      initialInKindDonations,
      finalBudget?.inKindDonations || interimBudget?.inKindDonations,
    ),
    budgetFile,
  };
};

interface ReportBudgetProps {
  isLegacy: boolean | undefined;
  reportId: number | string | undefined;
  answers: ReportAnswers;
  budget?: { expenses: string; current: string }[];
}

const StyledDiv = styled.div`
  background-color: ${COLORS.whiteBase};
  border-top: 1px solid ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayLight};
  padding-top: ${SPACERS.large};
  padding-bottom: ${SPACERS.large};
  .report-budget-header {
    padding-left: ${SPACERS.extraLarge};
    padding-right: ${SPACERS.extraLarge};
    padding-bottom: ${SPACERS.tiny};
  }
  .report-total-budget {
    display: flex;
    color: ${COLORS.whiteBase};
    justify-content: space-between;
    background-color: ${COLORS.grayDarkest};
    border-top: 1px solid ${COLORS.grayLight};
    padding: ${SPACERS.medium} ${SPACERS.extraLarge};
    .report-total-budget-amount {
      letter-spacing: 1px;
    }
  }
  .budget-files-row {
    display: block !important;
    .budget-files {
      display: flex;
      gap: ${SPACERS.tiny};
      justify-content: space-between;
      .budget-files-list {
        display: flex;
        flex-direction: column;
        gap: ${SPACERS.tiny};
      }
    }
  }
`;

const RenderBudgetFiles: React.FC<{
  files: (TrueImpactFile | string)[];
  description?: string | JSX.Element | JSX.Element[];
}> = ({ files, description }) => {
  return (
    <ReportBudgetRowWrapper className="budget-files-row">
      <div className="budget-files">
        <div>
          <TrueImpactText variant="body-small-bold">
            Budget notes
          </TrueImpactText>
          <TrueImpactText variant="body-small">
            {typeof description === 'string'
              ? renderTextWithParagraphs(description)
              : description || 'No description provided.'}
          </TrueImpactText>
        </div>
        <div>
          {files.map((f, i) => (
            <TrueImpactFileRender
              key={i}
              file={f}
              className="budget-files-list"
            />
          ))}
        </div>
      </div>
    </ReportBudgetRowWrapper>
  );
};

const ReportBudget: React.FC<ReportBudgetProps> = ({
  reportId,
  answers,
  budget,
  isLegacy,
}) => {
  const [legacyFile, setLegacyFile] = useState<BudgetFileSummary[]>([]);
  const [legacyNotes, setLegacyNotes] = useState<ReportNoteSummary[]>([]);

  useEffect(() => {
    if (!reportId) return;
    getReportNotes(reportId).then(response => {
      if (response.success && response.data) {
        setLegacyNotes(response.data);
      }
    });
    getBudgetFiles(reportId).then(response => {
      if (response.success && response.data) {
        setLegacyFile(response.data);
      }
    });
  }, [reportId]);

  const {
    directCost,
    overheadCost,
    inKindDonations,
    totalBudget,
    directCostVariance,
    overheadCostVariance,
    inKindDonationVariance,
    budgetFile,
  } = getBudgetFromAnswers(answers);

  /* Legacy support: If there is a budget from answers, use it,
  otherwise use the budget from the report */

  // in Kind Donations in Legacy = Direct In-Kind + In-Direct In-Kind
  const inKindDonationsLegacy =
    (Number(budget?.find(b => b.expenses === 'Direct In-Kind')?.current) || 0) +
    (Number(budget?.find(b => b.expenses === 'In-Direct In-Kind')?.current) ||
      0);

  const directCostDisplay = directCost
    ? getCostValue(directCost)
    : getCostValue(budget?.find(b => b.expenses === 'Direct Cash')?.current);
  const overheadCostDisplay = overheadCost
    ? getCostValue(overheadCost)
    : getCostValue(budget?.find(b => b.expenses === 'In-Direct Cash')?.current);
  const inKindDonationsDisplay = inKindDonations
    ? getCostValue(inKindDonations)
    : getCostValue(inKindDonationsLegacy.toString());
  const totalBudgetDisplay =
    typeof totalBudget === 'number' &&
    (directCost || overheadCost || inKindDonations)
      ? numberWithCommas(totalBudget.toFixed(2))
      : numberWithCommas(
          budget?.find(b => b.expenses === 'Total Program Cost')?.current,
        );

  const description = budgetFile?.description;
  // budgetFile can be one of:
  //  - { files: TrueImpactFile[], description }        -> use files array
  //  - { fileName, id, url, description }              -> a single file object
  //  - { fileName: '<serialized filename string>' }    -> legacy string form
  const files: (TrueImpactFile | string)[] | string | undefined =
    budgetFile?.files ||
    (budgetFile?.url ? [budgetFile] : budgetFile?.fileName);

  return (
    <StyledDiv>
      <TrueImpactText
        variant="heading-subheading"
        className="report-budget-header"
      >
        Budget
      </TrueImpactText>
      <ReportBudgetHeader />
      <ReportBudgetRow
        heading="Direct Cost"
        subheading={directCost || directCostDisplay}
        percentage={directCostVariance}
      />
      <ReportBudgetRow
        heading="Overhead Cost"
        subheading={overheadCost || overheadCostDisplay}
        percentage={overheadCostVariance}
      />
      <ReportBudgetRow
        heading="In-kind Donations"
        subheading={inKindDonations || inKindDonationsDisplay}
        percentage={inKindDonationVariance}
      />
      {files &&
      (typeof files === 'string' ? files.length > 2 : files.length > 0) ? (
        <RenderBudgetFiles
          files={typeof files === 'string' ? [files] : files}
          description={description}
        />
      ) : description ? (
        <RenderBudgetFiles files={[]} description={description} />
      ) : null}
      {isLegacy && (
        <RenderBudgetFiles
          files={mapTrueImpactFile(legacyFile)}
          description={renderTextWithParagraphs(
            legacyNotes.map(l => l.message).join('\n'),
          )}
        />
      )}
      <div className="report-total-budget">
        <TrueImpactText variant="body-bold" color="whiteBase">
          Total Budget
        </TrueImpactText>
        <TrueImpactText
          variant="body-bold"
          color="whiteBase"
          className="report-total-budget-amount"
        >
          ${totalBudgetDisplay}
        </TrueImpactText>
      </div>
    </StyledDiv>
  );
};

export default ReportBudget;
